import { useState, useMemo, useCallback, useRef } from 'react';

import Analytics from '@hh.ru/analytics-js';
import { Button, Tooltip, Tag, VSpacing, BottomSheet, BottomSheetFooter } from '@hh.ru/magritte-ui';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import ConversionNumber from 'bloko/blocks/conversion';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { formatToReactComponent } from 'bloko/common/trl';

import formatDate from 'Modules/formatDate';
import { useVacancyIdNonNullable } from 'src/components/ResumeCard/hooks/useVacancyId';
import translation from 'src/components/translation';
import { TagCallData, TagName } from 'src/models/candidatesList';

import styles from './index.less';

const TrlKeys = {
    successTag: 'employer.vacancyResponses.badge.phoneCall.success',
    missedTag: 'employer.vacancyResponses.badge.phoneCall.missed',
    successDropInfo: 'employer.vacancyResponses.badge.phoneCall.success.infotip',
    missedDropInfo: 'employer.vacancyResponses.badge.phoneCall.missed.infotip',
    duration: 'employer.vacancyResponses.badge.phoneCall.success.infotip.duration',
    durationMinutesOne: 'employer.vacancyResponses.badge.phoneCall.success.infotip.duration.one',
    durationMinutesSome: 'employer.vacancyResponses.badge.phoneCall.success.infotip.duration.some',
    durationMinutesMany: 'employer.vacancyResponses.badge.phoneCall.success.infotip.duration.many',
    callPageButton: 'employer.vacancyResponses.badge.phoneCall.infotip.callpage.link',
    applicantDidntCallButton: 'employer.vacancyResponses.badge.phoneCall.infotip.applicantDidntCall.link',
};

interface Props {
    data?: TagCallData;
    type?: TagName;
}

const FromPhoneCall: TranslatedComponent<Props> = ({ type, data, trls }) => {
    const vacancyId = useVacancyIdNonNullable();
    const [showDropDown, setShowDropInfo] = useState(false);
    const activatorRef = useRef<HTMLSpanElement>(null);

    const handleOpenDropInfo = useCallback(() => {
        setShowDropInfo(true);
    }, [setShowDropInfo]);

    const handleCloseDropInfo = useCallback(() => {
        setShowDropInfo(false);
    }, [setShowDropInfo]);

    const pickedUpPhone = type === TagName.IncomingCall;
    const dataQa = `response_badge_from_phone_call_${pickedUpPhone ? 'success' : 'missed'}`;

    const tooltipText = useMemo(() => {
        if (!data) {
            return null;
        }

        return formatToReactComponent(trls[TrlKeys[pickedUpPhone ? 'successDropInfo' : 'missedDropInfo']], {
            '{0}': formatDate(data.creationTime, 'dd'),
            '{1}': formatDate(data.creationTime, 'MMMM'),
            '{2}': formatDate(data.creationTime, 'HH:mm'),
        });
    }, [data, pickedUpPhone, trls]);

    if (!data) {
        return null;
    }

    const tooltipLeftButtonProps = {
        'data-qa': 'response_badge_from_call_callpage_link',
        Element: SPALink,
        to: `/employer/vacancyresponses/calls?vacancyId=${vacancyId}`,
        target: '_blank',
    };

    const tooltipRightButtonProps = {
        'data-qa': 'response_badge_from_call_applicant-didnt-call',
        onClick: () =>
            Analytics.sendHHEventButtonClick('callReportSubmit', {
                reason: 'wrongCall',
                callId: data.callId,
            }),
    };

    const callDuration = formatToReactComponent(trls[TrlKeys.duration], {
        '{0}': data.minutes,
        '{1}': (
            <ConversionNumber
                value={data.minutes}
                hasValue={false}
                one={trls[TrlKeys.durationMinutesOne]}
                some={trls[TrlKeys.durationMinutesSome]}
                many={trls[TrlKeys.durationMinutesMany]}
            />
        ),
    });

    return (
        <>
            <span
                data-qa={dataQa}
                onClick={handleOpenDropInfo}
                data-interactive
                className={styles.tagClickable}
                ref={activatorRef}
            >
                <Tag style={pickedUpPhone ? 'special' : 'negative'}>
                    {pickedUpPhone ? trls[TrlKeys.successTag] : trls[TrlKeys.missedTag]}
                </Tag>
            </span>
            <Tooltip
                placement="top-center"
                visible={showDropDown}
                showClose
                aria-label-close="close"
                onClose={handleCloseDropInfo}
                activatorRef={activatorRef}
            >
                <>
                    {tooltipText}
                    {data.minutes ? <div>{callDuration}</div> : null}
                    <VSpacing default={12} />
                    <div className={styles.tagTooltipButtonsWrapper}>
                        <Button mode="secondary" style="contrast" {...tooltipLeftButtonProps}>
                            {trls[TrlKeys.callPageButton]}
                        </Button>
                        <Button mode="tertiary" style="contrast" {...tooltipRightButtonProps}>
                            {trls[TrlKeys.applicantDidntCallButton]}
                        </Button>
                    </div>
                </>
            </Tooltip>
            <BottomSheet
                visible={showDropDown}
                onClose={handleCloseDropInfo}
                footer={
                    <BottomSheetFooter>
                        <Button mode="primary" style="accent" {...tooltipLeftButtonProps} stretched>
                            {trls[TrlKeys.callPageButton]}
                        </Button>
                        <Button mode="secondary" style="accent" {...tooltipRightButtonProps} stretched>
                            {trls[TrlKeys.applicantDidntCallButton]}
                        </Button>
                    </BottomSheetFooter>
                }
            >
                {tooltipText}
                {data.minutes ? <div>{callDuration}</div> : null}
            </BottomSheet>
        </>
    );
};

export default translation(FromPhoneCall);
